import { AnyAction, ListenerMiddlewareInstance, ThunkDispatch } from '@reduxjs/toolkit'
import { config } from '@config/config'
import { AppState } from '@redux/store'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import { authApi } from '@redux/queries/auth'
import { ApiCache } from '@redux/queries/apiCache'
import { claimRecordsApi } from '@redux/queries/claimRecords'
import { setMemberEligiblityCheckInProgress } from '@redux/slices/insuranceMemberSlice'
import { isSyncInsuranceMemberEligibilityRequired } from '@utils/insuranceMemberUtil'
import { isFetchBaseError, isGatewayTimeout } from '@utils/errorUtil'

type ListenerInstance = ListenerMiddlewareInstance<unknown, ThunkDispatch<unknown, unknown, AnyAction>, unknown>

/**
 * US flow - guest session left idle (e.g. 2 hrs) will require sync eligibility again.
 * On auth token fetch, sync eligiblity as required.
 */
export const initSyncMemberEligiblityListener = (listenerMiddleware: ListenerInstance) => {
  listenerMiddleware.startListening({
    matcher: authApi.endpoints.getAuthToken.matchFulfilled,

    effect: async (action, listenerApi) => {
      if (config.getFeatures()?.disableInsuranceMemberEligibilityCheck) return

      const state = listenerApi.getState() as AppState
      const session = state.connector.session

      const claimRecordId = action.payload.claimRecordId
      const isGuest = session?.isGuest

      if (!claimRecordId || !isGuest) return

      listenerApi.dispatch(setMemberEligiblityCheckInProgress(true))

      let claimRecord: ClaimRecord | undefined

      try {
        claimRecord = await listenerApi
          .dispatch(claimRecordsApi.endpoints.getClaimRecordNoCache.initiate(claimRecordId))
          .unwrap()
      } catch (error) {
        listenerApi.dispatch(setMemberEligiblityCheckInProgress(false))
        return
      }

      if (!isSyncInsuranceMemberEligibilityRequired(claimRecord?.claimData?.useInsuranceStatus)) {
        listenerApi.dispatch(setMemberEligiblityCheckInProgress(false))
        return
      }

      const performCheck = async () => {
        await listenerApi
          .dispatch(claimRecordsApi.endpoints.syncInsuranceMemberEligibility.initiate(claimRecordId))
          .unwrap()

        listenerApi.dispatch(setMemberEligiblityCheckInProgress(false))
      }

      const checkWithRetry = async () => {
        try {
          await performCheck()
        } catch (error) {
          if (isFetchBaseError(error) && isGatewayTimeout(error.status)) {
            await performCheck()
          } else {
            throw error
          }
        }
      }

      try {
        // invalidate cached insurance benefits to ensure refetch gets latest from B/E
        await ApiCache.delete('getInsurancePlans')
        checkWithRetry()
      } catch (error) {
        listenerApi.dispatch(setMemberEligiblityCheckInProgress(false))
        console.warn('directbilling - sync insurance member eligiblity error', error)
      }
    },
  })
}
